/**
 * Deterministic finding_key → in-app route table.
 *
 * The founder-facing "Accept & take me there" flow needs to know where
 * to send the operator when they act on a recommendation. Because the
 * backend intentionally returns only human-language ``recommended_action``
 * text, the routing lookup lives here — but it is *not* content
 * generation: this file only maps known keys to known routes. Any
 * unknown key falls back to ``null`` so the card renders the safe
 * "unsupported action" affordance.
 */
export type AkinexActionRoute = {
  /** In-app path to navigate to. */
  path: string;
  /** Short label shown on the primary Accept button. */
  label: string;
};

const ROUTES: Record<string, AkinexActionRoute> = {
  // ─── Executive Advisor ─────────────────────────────────────────────
  assets_awaiting_review: {
    path: "/campaigns/review",
    label: "Open Review Queue",
  },
  prompts_ready_to_render: {
    path: "/campaigns",
    label: "Open Campaigns",
  },
  stalled_campaigns: {
    path: "/campaigns",
    label: "Open Campaigns",
  },

  // ─── Operations Sentinel ───────────────────────────────────────────
  publishing_failed: {
    path: "/publishing/calendar",
    label: "Open Mission Control",
  },
  publishing_retrying: {
    path: "/publishing/calendar",
    label: "Open Mission Control",
  },
  stuck_renders: {
    path: "/workspace/diagnostics",
    label: "Open Diagnostics",
  },
  approved_not_vaulted: {
    path: "/vault",
    label: "Open Vault",
  },

  // ─── Knowledge Curator ─────────────────────────────────────────────
  brain_missing: {
    path: "/business-brain",
    label: "Open Business Brain",
  },
  brain_incomplete: {
    path: "/business-brain",
    label: "Open Business Brain",
  },
  brain_unapproved: {
    path: "/business-brain",
    label: "Open Business Brain",
  },
};

/**
 * Look up the in-app route for a finding key. Returns ``null`` when the
 * key is unknown to this frontend build — in that case the founder is
 * still able to Accept (which records intent on the timeline) but no
 * navigation is offered. This is the ``unsupported-action fallback``
 * exercised by the frontend test suite.
 */
export function resolveActionRoute(findingKey: string): AkinexActionRoute | null {
  return ROUTES[findingKey] ?? null;
}
